/// <amd-module name="Wit/Common/WorkItemDialogs/Components/ProcessWorkItemsDialog" />
import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Dialog } from "VSSUI/Dialog";
import { IListSelection, ListSelection } from "VSSUI/List";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { ProgressBar } from "VSSUI/ProgressBar";
import { Spinner, SpinnerOrientation, SpinnerSize } from "VSSUI/Spinner";
import { executeCallbackForTargetIdsInBatches } from "Wit/Common/WorkItemDialogs/Components/BatchExecutionHelper";
import * as Resources from "Wit/Common/WorkItemDialogs/Resources";
import { PageSizes } from "Wit/WorkItemModel/OM";

export interface IProcessWorkItemsDialogProps {
    workItemIds: number[];
    onDismiss: () => void;
    processWorkItems: (workItemIds: number[]) => Promise<boolean>;
    callBack?: () => void;
    itemsPerStep?: number;
    maximumAmountOfSimultaneousRequests?: number;
    allowTempIds?: boolean;
}

interface IProcessWorkItemsDialogState {
    errorMessage: string | undefined;
    operationStatus: ProcessWorkItemsDialogOperationStatus;
}

enum ProcessWorkItemsDialogOperationStatus {
    /**
     * The operation is waiting in a queue and has not yet started.
     */
    Queued = 1,
    /**
     * The operation is currently in progress.
     */
    InProgress = 2,
    /**
     * The operation has completed.
     */
    Completed = 3,
    /**
     * The operation has failed. Check for an error message.
     */
    Failed = 4
}

export function restoreSelection(selectionCopy: { beginIndex: number; endIndex: number; }[], selection: IListSelection | ListSelection) {
    const needToProcessMultipleIntervals = selectionCopy.length > 1;
    if (needToProcessMultipleIntervals) {
        selection.clear();
    }
    for (const range of selectionCopy) {
        selection.select(range.beginIndex, range.endIndex - range.beginIndex + 1, needToProcessMultipleIntervals);
    }
}

export class ProcessWorkItemsDialog extends VssComponent<IProcessWorkItemsDialogProps, IProcessWorkItemsDialogState> {
    private stepNumber = new ObservableValue<number>(0);
    private stepCount = new ObservableValue<number>(0);
    private failedIds: number[] | undefined = undefined;
    private defaultMaximumAmountOfSimultaneousRequests = 5;

    public constructor(props: IProcessWorkItemsDialogProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            errorMessage: undefined,
            operationStatus: ProcessWorkItemsDialogOperationStatus.Queued
        };
    }

    public async componentDidMount(): Promise<void> {
        this.setState({ operationStatus: ProcessWorkItemsDialogOperationStatus.InProgress });
        try {
            const succeeded = await executeCallbackForTargetIdsInBatches(
                this.props.workItemIds,
                subsetTargetIds => this.processWorkItemsUsingBatches(subsetTargetIds),
                this.stepCount,
                this.stepNumber,
                this.props.maximumAmountOfSimultaneousRequests ?? this.defaultMaximumAmountOfSimultaneousRequests,
                this.props.itemsPerStep,
                this.props.allowTempIds
            );

            if (succeeded) {
                this.setState({ operationStatus: ProcessWorkItemsDialogOperationStatus.Completed });
                this.props.callBack?.();
                this.props.onDismiss();
            } else {
                this.setState({ operationStatus: ProcessWorkItemsDialogOperationStatus.Failed });
            }
        } catch (error) {
            this.failedIds = [...new Set([...(this.failedIds ?? []), ...this.props.workItemIds])];
            this.setState({ operationStatus: ProcessWorkItemsDialogOperationStatus.Failed, errorMessage: error.message });
        }
    }

    private processWorkItemsUsingBatches = async (workItemIds: number[]): Promise<boolean> => {
        try {
            return this.props.processWorkItems(workItemIds);
        } catch (error) {
            this.failedIds = [...new Set([...(this.failedIds ?? []), ...workItemIds])];
            this.setState({ operationStatus: ProcessWorkItemsDialogOperationStatus.Failed, errorMessage: error.message });
            return false;
        }
    };

    public render() {
        return (
            <Observer stepNumber={this.stepNumber} stepCount={this.stepCount}>
                {(observableProps: { stepNumber: number; stepCount: number }) => {
                    const { title, content } = this.getContent(observableProps);

                    return (
                        <Dialog
                            titleProps={{ text: title }}
                            onDismiss={this.props.onDismiss}
                            escDismiss={true}
                            lightDismiss={false}
                            footerButtonProps={[
                                {
                                    text: Resources.OK,
                                    onClick: this.props.onDismiss,
                                    primary: true,
                                    disabled: this.state.operationStatus === ProcessWorkItemsDialogOperationStatus.InProgress
                                }
                            ]}
                        >
                            {content}
                        </Dialog>
                    );
                }}
            </Observer>
        );
    }

    private getContent(observableProps: { stepNumber: number; stepCount: number }) {
        let title = Resources.ProcessingWorkItemsTitle;
        let content = <Spinner className="padding-vertical-8" size={SpinnerSize.large} />;

        switch (this.state.operationStatus) {
            case ProcessWorkItemsDialogOperationStatus.InProgress: {
                const processingTemlate = Resources.ProcessingWorkItemsProcessingMessage;
                const stepMultiplier = this.props.itemsPerStep ?? PageSizes.QUERY;
                const from = observableProps.stepNumber * stepMultiplier + 1;
                const requestCount = this.props.maximumAmountOfSimultaneousRequests ?? this.defaultMaximumAmountOfSimultaneousRequests;
                const to = (observableProps.stepNumber + requestCount) * stepMultiplier;
                const processingMessage = format(
                    processingTemlate,
                    from,
                    to < this.props.workItemIds.length ? to : this.props.workItemIds.length,
                    this.props.workItemIds.length
                );
                content = (
                    <>
                        <ProgressBar className="flex-self-stretch" currentValue={observableProps.stepNumber} maxValue={observableProps.stepCount} />
                        <Spinner className="font-size-ms padding-vertical-16" label={processingMessage} orientation={SpinnerOrientation.row} />
                    </>
                );
                break;
            }
            case ProcessWorkItemsDialogOperationStatus.Completed: {
                const successTitleTemplate = Resources.ProcessingWorkItemsSucceededTitle;
                const successMessageTemplate = Resources.ProcessingWorkItemsSucceededMessage;
                title = successTitleTemplate;
                content = (
                    <div className="scroll-auto flex-grow flex-column">
                        <p>{format(successMessageTemplate, this.props.workItemIds.length)}</p>
                    </div>
                );
                break;
            }
            case ProcessWorkItemsDialogOperationStatus.Failed: {
                const errorTitleTemplate = Resources.ProcessingWorkItemsFailedTitle;
                const errorMessageTemplate = Resources.ProcessingWorkItemsFailedMessage;
                title = errorTitleTemplate;
                content = (
                    <div className="scroll-auto flex-grow flex-column">
                        {this.state.errorMessage && (
                            <MessageCard className="flex-noshrink" severity={MessageCardSeverity.Error}>
                                {this.state.errorMessage}
                            </MessageCard>
                        )}
                        <p>
                            {format(
                                errorMessageTemplate,
                                this.failedIds
                                    ? this.failedIds.join(Resources.ProcessingWorkItemsFailedSeparator)
                                    : this.props.workItemIds.join(Resources.ProcessingWorkItemsFailedSeparator)
                            )}
                        </p>
                    </div>
                );
                break;
            }
            case ProcessWorkItemsDialogOperationStatus.Queued:
            default: {
                break;
            }
        }
        return { title, content };
    }
}
